// =============================================================================
// src/lib/transientFetch.js - retry PostgREST answers that mean "not run, try
// again", so a cold database does not paint error boxes on the dashboard.
//
// Why this exists (nightly error triage, Oct 2026): after the project sits
// idle, the first sign-in lands on a cold database. Realtime re-initialises
// its tenant (creating realtime.messages partitions - DDL), which fires
// several "reload schema" notices at PostgREST; PostgREST resets its pool and
// re-reads the schema while the dashboard is firing ~40 requests. Two errors
// come back that say nothing about the request itself:
//
//   500 + code 57014  "canceling statement due to statement timeout" - the
//                     statement was cancelled and its transaction rolled back,
//                     so nothing happened. Seen on get_role_worklist,
//                     practice_worklist_insights and eod_sweep (Oct 6 and 8).
//   503 + PGRST000-003 PostgREST could not reach the database or load its
//                     schema cache; the request never ran. Seen on the Recall
//                     tab RPCs (Oct 8). postgrest-js already retries these for
//                     GET/HEAD, but supabase.rpc() is a POST, so it did not.
//
// Both are safe to retry for ANY method: the first attempt left no effects.
// Everything else (4xx, other 5xx, network failures, non-PostgREST URLs)
// passes through untouched - a real error still reaches the screen.
//
// Heavy lane (nightly triage, Oct 9 2026): one retry 1.2 s later was not
// enough. The overload after idle lasts 10-25 s, and in 4 of 6 bursts the
// retry timed out too. Three dashboard RPCs did most of the failing:
// get_role_worklist, practice_worklist_insights and eod_sweep (each scans
// the whole worklist; 0.1-0.2 s warm, 8 s+ on a starved database). They now:
//   1. wait until the rest of the page's REST requests have answered (at
//      most HEAVY_GATE_MS), so they do not compete with the first paint;
//   2. run one at a time, so they do not compete with each other;
//   3. get a retry plan that outlasts the overload (HEAVY_RETRY_DELAYS).
// Warm, the three take well under a second in a row, so nothing visible
// changes on a normal load.
//
// Wired as global.fetch in supabaseClient.js. Guarded by
// src/lib/__tests__/transientFetch.contract.test.mjs.
// =============================================================================

const REST_PATH = "/rest/v1/";
const POOL_CODES = new Set(["PGRST000", "PGRST001", "PGRST002", "PGRST003"]);

// Delay before each retry (ms). Statement timeouts get one retry (a real slow
// query should not cost the user 3 x 8 seconds); not-run 503s get two.
export const RETRY_DELAYS = { timeout: [1200], unavailable: [800, 2000] };

// RPCs that go through the heavy lane. Every name must be a function defined
// in supabase/migrations and called from src (contract test checks both).
export const HEAVY_RPCS = ["get_role_worklist", "practice_worklist_insights", "eod_sweep"];

// Heavy lane retry plan. Worst case with the 8 s statement timeout:
// 8 + 3 + 8 + 8 + 8 = 35 s before the panel shows its error, which covers
// the 10-25 s overload seen in the logs. The contract test pins that range.
export const HEAVY_RETRY_DELAYS = { timeout: [3000, 8000], unavailable: [1500, 4000, 8000] };

// Longest a heavy RPC waits for the rest of the page before it starts anyway.
export const HEAVY_GATE_MS = 4000;

function rpcNameOf(url) {
  const i = url.indexOf(REST_PATH + "rpc/");
  if (i === -1) return null;
  const rest = url.slice(i + REST_PATH.length + 4);
  return rest.split(/[?#/]/)[0] || null;
}

export function isHeavyRpc(url) {
  const name = rpcNameOf(url);
  return !!name && HEAVY_RPCS.indexOf(name) !== -1;
}

function methodOf(input, init) {
  const m = (init && init.method) || (input && typeof input === "object" && input.method) || "GET";
  return String(m).toUpperCase();
}

function urlOf(input) {
  if (typeof input === "string") return input;
  if (input && typeof input === "object" && "url" in input) return String(input.url);
  return String(input || "");
}

async function codeOf(res) {
  try {
    const body = await res.clone().json();
    return body && body.code ? String(body.code) : null;
  } catch (e) {
    return null;
  }
}

// Which retry budget (if any) applies to this response.
//   "timeout"     - 500 with Postgres 57014
//   "unavailable" - 503 from PostgREST on a non-GET/HEAD (postgrest-js
//                   already retries GET/HEAD 503s itself; doing it here too
//                   would multiply the attempts)
//   null          - not transient; hand it back as is
export async function transientKind(method, res) {
  if (!res) return null;
  if (res.status === 500) return (await codeOf(res)) === "57014" ? "timeout" : null;
  if (res.status === 503) {
    if (method === "GET" || method === "HEAD") return null;
    const code = await codeOf(res);
    return code === null || POOL_CODES.has(code) ? "unavailable" : null;
  }
  return null;
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Tracks REST requests that are not in the heavy lane, so a heavy RPC can
// wait for them, and runs heavy attempts one at a time.
function createLane({ gateMs, timer }) {
  let others = 0;
  let idleWaiters = [];
  let busy = false;
  let queue = [];

  const notifyIdle = () => {
    if (others !== 0) return;
    const w = idleWaiters;
    idleWaiters = [];
    w.forEach((fn) => fn());
  };

  return {
    async trackOther(run) {
      others += 1;
      try { return await run(); } finally { others -= 1; notifyIdle(); }
    },
    // Resolves when no other REST request is in flight, or after gateMs.
    waitForIdle() {
      if (others === 0) return Promise.resolve();
      return new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        idleWaiters.push(finish);
        timer(gateMs).then(finish);
      });
    },
    // One heavy attempt at a time, first come first served.
    async exclusive(run) {
      if (busy) await new Promise((resolve) => queue.push(resolve));
      busy = true;
      try { return await run(); } finally {
        const next = queue.shift();
        if (next) next(); else busy = false;
      }
    },
  };
}

export function createTransientRetryFetch(baseFetch, {
  delays = RETRY_DELAYS,
  heavyDelays = HEAVY_RETRY_DELAYS,
  heavyGateMs = HEAVY_GATE_MS,
  sleep = wait,
  timer = wait,
} = {}) {
  const lane = createLane({ gateMs: heavyGateMs, timer });

  async function withRetries(input, init, method, plan, attemptOnce) {
    let res = await attemptOnce();
    let attempt = 0;
    for (;;) {
      const kind = await transientKind(method, res);
      if (!kind) return res;
      const steps = plan[kind] || [];
      if (attempt >= steps.length) return res;
      if (init && init.signal && init.signal.aborted) return res;
      await sleep(steps[attempt]);
      attempt += 1;
      res = await attemptOnce();
    }
  }

  return async function transientRetryFetch(input, init) {
    const url = urlOf(input);
    if (url.indexOf(REST_PATH) === -1) return baseFetch(input, init);
    const method = methodOf(input, init);
    if (!isHeavyRpc(url)) {
      return lane.trackOther(() => withRetries(input, init, method, delays, () => baseFetch(input, init)));
    }
    // Heavy lane: let the rest of the page answer first, then one at a time.
    // The lane is held per attempt, not across the retry sleep, so a heavy
    // RPC that is backing off does not block the others.
    await lane.waitForIdle();
    return withRetries(input, init, method, heavyDelays, () => lane.exclusive(() => baseFetch(input, init)));
  };
}

export const transientRetryFetch = createTransientRetryFetch((input, init) => fetch(input, init));
